import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

let scriptPromise = null;

function loadPayPal(clientId) {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.paypal) return resolve(window.paypal);
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture`;
    script.async = true;
    script.onload = () => resolve(window.paypal);
    script.onerror = reject;
    document.body.appendChild(script);
  });
  return scriptPromise;
}

export default function PayPalButton({ courseId, seats = 1, purchaseAs = "personal", onPaid }) {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const propsRef = useRef({ courseId, seats, purchaseAs });
  propsRef.current = { courseId, seats, purchaseAs };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/paypal/config");
        const clientId = r.data?.client_id;
        if (!clientId || clientId.startsWith("sb-paypal-sandbox-client-id")) {
          setError("PayPal not configured. Add real PAYPAL_CLIENT_ID and PAYPAL_SECRET to backend .env to enable checkout.");
          return;
        }
        const paypal = await loadPayPal(clientId);
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = "";
        paypal.Buttons({
          style: { color: "black", shape: "rect", layout: "vertical", label: "pay" },
          createOrder: async () => {
            const { courseId: cid, seats: s, purchaseAs: pa } = propsRef.current;
            const o = await api.post("/paypal/create-order", { course_id: cid, seats: s, purchase_as: pa });
            return o.data.order_id;
          },
          onApprove: async (data) => {
            try {
              const { courseId: cid, seats: s, purchaseAs: pa } = propsRef.current;
              const c = await api.post("/paypal/capture-order", { order_id: data.orderID, course_id: cid, seats: s, purchase_as: pa });
              if (c.data.status === "COMPLETED") {
                toast.success("Payment completed");
                onPaid?.(c.data);
              } else {
                toast.error("Payment not completed: " + c.data.status);
              }
            } catch {
              toast.error("Capture failed");
            }
          },
          onError: (err) => {
            console.error(err);
            toast.error("PayPal error");
          },
        }).render(containerRef.current);
        setReady(true);
      } catch (e) {
        setError("Could not load PayPal");
      }
    })();
    return () => { cancelled = true; };
  }, [onPaid]);

  if (error) {
    return (
      <div className="border border-[#854D0E] bg-[#FFF7ED] p-4 text-sm" data-testid="paypal-not-configured">
        <div className="overline text-[#854D0E] mb-1">PayPal not configured</div>
        <div className="text-[#52525B]">{error}</div>
      </div>
    );
  }
  return <div ref={containerRef} data-testid="paypal-button-container">{!ready && <div className="overline">Loading PayPal…</div>}</div>;
}
